import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { createCustomTemplateFormMessages } from './createCustomTemplateForm.messages';
import {
  CreateCustomTemplateFormSchema,
  type CreateCustomTemplateFormValues,
} from 'schemas';

import type { RoleTemplate, StyleTemplateWithUrls } from 'schemas';
import {
  Form,
  FormField,
  FormItem,
  FormLabel,
  FormControl,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Heading3, Muted } from '@/components/Typography';
import { useState, useRef } from 'react';
import { fetchRoleTemplates } from '@/api/services/role-templates/fetchRoleTemplates';
import { fetchStyleTemplates } from '@/api/services/style-templates/fetchStyleTemplates';
import { useLoadOnce } from '@/hooks/useLoadOnce';
import { toast } from 'sonner';
import { ApiError } from '@/api/ApiError';
import { UNKNOWN_ERROR_API_ERROR } from '@/api/helpers/unknownError';
import { EntityCard } from '@/components/EntityCard';
import { transformStyleTemplateToEntityCard } from '@/pages/StyleTemplatesPage/helpers/transformStyleTemplateToEntityCard';

interface CreateCustomTemplateFormProps {
  onSubmit: (formData: CreateCustomTemplateFormValues) => Promise<void>;
  isSubmitting?: boolean;
  onCancel?: () => void;
}

export function CreateCustomTemplateForm({
  onSubmit,
  isSubmitting = false,
  onCancel,
}: CreateCustomTemplateFormProps) {
  const [roleTemplates, setRoleTemplates] = useState<RoleTemplate[]>([]);
  const [loadingRoleTemplates, setLoadingRoleTemplates] = useState(true);
  const [styleTemplates, setStyleTemplates] = useState<StyleTemplateWithUrls[]>(
    []
  );
  const [loadingStyleTemplates, setLoadingStyleTemplates] = useState(true);
  const hasSetDefaultRole = useRef(false);

  const form = useForm<CreateCustomTemplateFormValues>({
    resolver: zodResolver(CreateCustomTemplateFormSchema),
    defaultValues: {
      title: '',
      description: '',
      style_template_id: '',
      role_template_ids: [''],
    },
  });

  // Load role templates on mount
  useLoadOnce(() => {
    const loadRoleTemplates = async () => {
      try {
        setLoadingRoleTemplates(true);
        const templates = await fetchRoleTemplates();
        setRoleTemplates(templates);

        // Set "Child" as default for role 1 after templates are loaded
        if (templates.length > 0 && !hasSetDefaultRole.current) {
          const currentRoleIds = form.getValues('role_template_ids');
          // Only set default if first role is empty
          if (currentRoleIds && currentRoleIds[0] === '') {
            const childTemplate = templates.find(
              (template) => template.title === 'Child'
            );
            if (childTemplate) {
              form.setValue('role_template_ids.0', childTemplate.id, {
                shouldValidate: false,
                shouldDirty: false,
              });
              hasSetDefaultRole.current = true;
            }
          }
        }
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoadingRoleTemplates(false);
      }
    };

    loadRoleTemplates();
  });

  // Load style templates on mount
  useLoadOnce(() => {
    const loadStyleTemplates = async () => {
      try {
        setLoadingStyleTemplates(true);
        const templates = await fetchStyleTemplates();
        setStyleTemplates(templates);
      } catch (err) {
        const apiError =
          err instanceof ApiError ? err : UNKNOWN_ERROR_API_ERROR;
        toast.error(apiError.message);
      } finally {
        setLoadingStyleTemplates(false);
      }
    };

    loadStyleTemplates();
  });

  const addRoleTemplate = () => {
    const currentIds = form.getValues('role_template_ids');
    // Restrict to max 3 role templates
    if (currentIds.length < 3) {
      form.setValue('role_template_ids', [...currentIds, '']);
    }
  };

  const removeRoleTemplate = (index: number) => {
    const currentIds = form.getValues('role_template_ids');
    const removedId = currentIds[index];
    const newIds = currentIds.filter((_, i: number) => i !== index);
    form.setValue('role_template_ids', newIds);

    if (removedId && removedId.trim() !== '') {
      form.trigger('role_template_ids');
    }
  };

  const selectedRoleTemplateIds = form.watch('role_template_ids');

  return (
    <div className="mx-auto w-full">
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-6"
          aria-label={createCustomTemplateFormMessages.formAriaLabel}
        >
          {/* Story About Section */}
          <div className="mb-12 space-y-4">
            <Heading3>
              {createCustomTemplateFormMessages.storyAboutHeading}
            </Heading3>

            {/* Title Field */}
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem className="mb-10">
                  <FormLabel className="mb-2">
                    {createCustomTemplateFormMessages.titleLabel}
                  </FormLabel>
                  <FormControl>
                    <Input
                      placeholder={
                        createCustomTemplateFormMessages.titlePlaceholder
                      }
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description Field */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="mb-2">
                    {createCustomTemplateFormMessages.descriptionLabel}
                  </FormLabel>
                  <Muted>
                    {createCustomTemplateFormMessages.descriptionHelpText}
                  </Muted>
                  <FormControl>
                    <Textarea
                      placeholder={
                        createCustomTemplateFormMessages.descriptionPlaceholder
                      }
                      disabled={isSubmitting}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Role Templates Section */}
          <div className="space-y-4">
            <Heading3 className="mb-4">
              {createCustomTemplateFormMessages.rolesHeading}
            </Heading3>

            {loadingRoleTemplates ? (
              <div className="py-4 text-center">
                <Muted>
                  {createCustomTemplateFormMessages.loadingRoleTemplates}
                </Muted>
              </div>
            ) : (
              <div className="space-y-2">
                {selectedRoleTemplateIds.map((_, index: number) => (
                  <FormField
                    key={index}
                    control={form.control}
                    name={`role_template_ids.${index}`}
                    render={({ field }) => {
                      const selectedTemplate = roleTemplates.find(
                        (template) => template.id === field.value
                      );

                      return (
                        <FormItem className="mb-4 space-y-2">
                          <div className="flex items-end justify-between">
                            <FormLabel>Character {index + 1}</FormLabel>
                            {selectedRoleTemplateIds.length > 1 && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                onClick={() => removeRoleTemplate(index)}
                                disabled={isSubmitting}
                                aria-label={
                                  createCustomTemplateFormMessages.removeRoleAriaLabel
                                }
                              >
                                {
                                  createCustomTemplateFormMessages.removeRoleButton
                                }
                              </Button>
                            )}
                          </div>
                          {index === 0 && (
                            <div className="mb-2">
                              <Muted>
                                {
                                  createCustomTemplateFormMessages.rolesDescriptionLine1
                                }
                              </Muted>
                              <Muted>
                                {
                                  createCustomTemplateFormMessages.rolesDescriptionLine2
                                }
                              </Muted>
                            </div>
                          )}
                          <Select
                            value={field.value}
                            onValueChange={field.onChange}
                            disabled={isSubmitting}
                          >
                            <FormControl>
                              <SelectTrigger className="w-full text-left">
                                <SelectValue
                                  placeholder={
                                    createCustomTemplateFormMessages.roleTemplatePlaceholder
                                  }
                                >
                                  {selectedTemplate
                                    ? selectedTemplate.title
                                    : null}
                                </SelectValue>
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {roleTemplates.map((template) => (
                                <SelectItem
                                  key={template.id}
                                  value={template.id}
                                  className="text-left"
                                >
                                  <div className="text-left">
                                    <div className="font-medium">
                                      {template.title}
                                    </div>
                                    <div className="text-muted-foreground text-sm">
                                      {template.description}
                                    </div>
                                  </div>
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      );
                    }}
                  />
                ))}
                <Button
                  type="button"
                  variant="outline"
                  onClick={addRoleTemplate}
                  className="mb-6 w-full"
                  disabled={isSubmitting || selectedRoleTemplateIds.length >= 3}
                  aria-label={createCustomTemplateFormMessages.addRoleAriaLabel}
                >
                  {createCustomTemplateFormMessages.addRoleButton}
                </Button>
              </div>
            )}
          </div>

          {/* Style Template Section */}
          <div className="space-y-4">
            <Heading3>
              {createCustomTemplateFormMessages.illustrationStyleHeading}
            </Heading3>
            <Muted className="mb-2">
              {createCustomTemplateFormMessages.styleTemplateDescription}
            </Muted>
            {/* Style Template Field */}
            <FormField
              control={form.control}
              name="style_template_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">
                    {createCustomTemplateFormMessages.styleTemplateLabel}
                  </FormLabel>

                  {loadingStyleTemplates ? (
                    <div className="py-4 text-center">
                      <Muted>
                        {createCustomTemplateFormMessages.loadingStyleTemplates}
                      </Muted>
                    </div>
                  ) : (
                    <div className="overflow-x-auto p-2">
                      <div className="flex gap-6">
                        {styleTemplates.map((template) => {
                          const entityCardProps =
                            transformStyleTemplateToEntityCard(template);
                          const isSelected = field.value === template.id;
                          return (
                            <div
                              key={template.id}
                              onClick={() => {
                                if (!isSubmitting) {
                                  field.onChange(template.id);
                                }
                              }}
                              className={`min-w-[280px] max-w-[280px] flex-shrink-0 ${
                                isSubmitting
                                  ? 'pointer-events-none cursor-not-allowed opacity-50'
                                  : 'cursor-pointer'
                              }`}
                              role="button"
                              tabIndex={isSubmitting ? -1 : 0}
                              aria-disabled={isSubmitting}
                              onKeyDown={(e) => {
                                if (isSubmitting) return;
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  field.onChange(template.id);
                                }
                              }}
                              aria-label={`Select style template: ${template.title}`}
                            >
                              <EntityCard
                                {...entityCardProps}
                                actions={[]}
                                className={
                                  isSelected
                                    ? 'ring-primary ring-4 ring-offset-2'
                                    : ''
                                }
                              />
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          {/* Form Actions */}
          <div className="flex gap-4 pt-6">
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                {createCustomTemplateFormMessages.cancelButton}
              </Button>
            )}
            <Button
              type="submit"
              disabled={isSubmitting || !form.formState.isValid}
              className="flex-1"
            >
              {isSubmitting
                ? createCustomTemplateFormMessages.creating
                : createCustomTemplateFormMessages.createTemplateButton}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
}
